<template>
    <template v-if="component.customData.action && component.customData.action.type === ButtonActionType.Link">
        <a :href="component.customData.action.values ?? undefined"
            :class="[component.styles.cssClass, component.customData.buttonSize, getButtonFullWidth]" target="_blank"
            class="rd-btn-link" :style="{
                '--rd-base-font-family': component.styles.fontFamily,
                '--rd-input-border-radius': component.styles.borderRadius && component.styles.borderRadius + 'px',
                '--rd-button-text-color': !isNeutralButton && component.styles.fontColor ? component.styles.fontColor : '',
                '--rd-button-hover-color': !isNeutralButton && component.styles.hoverColor ? component.styles.hoverColor : '',
                '--rd-button-hover-text-color': !isNeutralButton && component.styles.hoverTextColor ? component.styles.hoverTextColor : '',
                '--rd-button-color': !isNeutralButton && component.styles.backgroundColor ? component.styles.backgroundColor : '',
                '--rd-button-outline-color': !isNeutralButton && component.styles.outlineColor ? component.styles.outlineColor : '',
                '--rd-button-outline-weight': !isNeutralButton && component.styles.outlineWeight ? component.styles.outlineWeight + 'px' : '',
                '--rd-neutral-button-color': (isNeutralButton && component.styles.backgroundColor) ? component.styles.backgroundColor : '',
                '--rd-neutral-button-text-color': (isNeutralButton && component.styles.fontColor) ? component.styles.fontColor : '',
                '--rd-neutral-button-hover-color': (isNeutralButton && component.styles.hoverColor) ? component.styles.hoverColor : '',
                '--rd-neutral-button-hover-text-color': (isNeutralButton && component.styles.hoverTextColor) ? component.styles.hoverTextColor : ''
            }">
            <i v-if="component.customData.icon && component.customData.iconPosition == 'left'" :class="[`fa-solid fa-${component.customData.icon}`,
            { 'rd-me-1': component.customData.text }]" aria-hidden="true"></i>

            {{ component.customData.text }}

            <i v-if="component.customData.icon && component.customData.iconPosition == 'right'" :class="[`fa-solid fa-${component.customData.icon}`,
            { 'rd-ms-1': component.customData.text }]" aria-hidden="true"></i>
        </a>
    </template>

    <template v-else-if="showButton">
        <button type="button"
            :class="[component.styles.cssClass, component.customData.buttonSize, isNextOrSubmitButton, getButtonFullWidth, getProcessing]"
            :style="{
                '--rd-base-font-family': component.styles.fontFamily,
                '--rd-input-border-radius': component.styles.borderRadius && component.styles.borderRadius + 'px',
                '--rd-button-text-color': !isNeutralButton && component.styles.fontColor ? component.styles.fontColor : '',
                '--rd-button-hover-color': !isNeutralButton && component.styles.hoverColor ? component.styles.hoverColor : '',
                '--rd-button-hover-text-color': !isNeutralButton && component.styles.hoverTextColor ? component.styles.hoverTextColor : '',
                '--rd-button-color': !isNeutralButton && component.styles.backgroundColor ? component.styles.backgroundColor : '',
                '--rd-button-outline-color': !isNeutralButton && component.styles.outlineColor ? component.styles.outlineColor : '',
                '--rd-button-outline-weight': !isNeutralButton && component.styles.outlineWeight ? component.styles.outlineWeight + 'px' : '',
                '--rd-neutral-button-color': (isNeutralButton && component.styles.backgroundColor) ? component.styles.backgroundColor : '',
                '--rd-neutral-button-text-color': (isNeutralButton && component.styles.fontColor) ? component.styles.fontColor : '',
                '--rd-neutral-button-hover-color': (isNeutralButton && component.styles.hoverColor) ? component.styles.hoverColor : '',
                '--rd-neutral-button-hover-text-color': (isNeutralButton && component.styles.hoverTextColor) ? component.styles.hoverTextColor : ''
            }" @click.stop="handleAction" :disabled="disabled">

            <svg v-if="processing" class="rd-animate-spin -rd-ml-1 rd-mr-2 rd-h-4 rd-w-4"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="rd-opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                </path>
            </svg>

            <i v-if="component.customData.icon && component.customData.iconPosition == 'left'" :class="[`fa-solid fa-${component.customData.icon}`,
            { 'rd-me-1': component.customData.text }]" aria-hidden="true"></i>

            <template v-if="hasButtonTextSlot">
                <slot name="buttonText"></slot>
            </template>
            <template v-else>
                {{ component.customData.text }}
            </template>

            <i v-if="component.customData.icon && component.customData.iconPosition == 'right'" :class="[`fa-solid fa-${component.customData.icon}`,
            { 'rd-ms-1': component.customData.text }]" aria-hidden="true"></i>
        </button>
    </template>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { IButtonComponent, ButtonActionType } from "@/builder/components/button/ButtonInterface";
import { useRenderStore } from "@/builder/stores/renderStore";
import { useBuilderStore } from "@/builder/stores/builderStore";

export default defineComponent({
    name: "ButtonComponent",
    props: {
        component: {
            type: Object as PropType<IButtonComponent>,
            required: true
        }
    },
    setup() {
        const renderStore = useRenderStore();
        const builderStore = useBuilderStore();

        return { renderStore, builderStore };
    },
    data() {
        return {
            ButtonActionType
        }
    },
    computed: {
        disabled() {
            if (this.component.customData.action && !this.renderStore.builderMode) {
                switch (this.component.customData.action.type) {
                    case ButtonActionType.NextStep:
                        return this.renderStore.step >= this.renderStore.maxSteps;
                    case ButtonActionType.PrevStep:
                        return this.renderStore.step <= 0;
                    case ButtonActionType.Submit:
                        return this.renderStore.isLoading || this.renderStore.isProcessing;
                    case ButtonActionType.Link:
                        return false;
                    case ButtonActionType.Custom:
                        return false;
                }
            }

            return false;
        },

        showButton() {
            if (!this.component.customData.action) return true;

            // you can customize the display here....

            return true;
        },

        processing(): boolean {
            if (this.component.customData.action) {
                if (this.component.customData.action.type === ButtonActionType.Submit) {
                    //return store processing state
                    return this.renderStore.isProcessing;
                }
            }

            return false;
        },

        getProcessing() {
            return this.processing && 'btn-processing';
        },

        getButtonSize() {
            return this.component.customData.buttonSize;
        },

        getButtonFullWidth() {
            return this.component.styles.fullWidth && 'rd-w-full';
        },

        hasButtonTextSlot() {
            return !!this.$slots.buttonText;
        },

        isNeutralButton() {
            return this.component.styles?.cssClass && this.component.styles.cssClass.indexOf('neutral') !== -1;
        },

        isNextOrSubmitButton() {
            const buttonAction = this.component.customData.action?.type;
            let buttonCssClass;

            if (buttonAction === ButtonActionType.NextStep) {
                buttonCssClass = 'rd-btn-next-step';
            }
            else if (buttonAction === ButtonActionType.Submit) {
                buttonCssClass = 'rd-btn-submit-form';
            }

            return buttonCssClass;
        }
    },
    methods: {
        async handleAction() {
            if (!this.component.customData.action) return;

            switch (this.component.customData.action.type) {
                case ButtonActionType.NextStep:
                case ButtonActionType.PrevStep:
                    this.renderStore.moveStep(this.component.customData.action.type);
                    break;
                case ButtonActionType.Submit:
                    if (this.renderStore.builderMode) return;
                    this.renderStore.submitDonation();
                    break;
                case ButtonActionType.Link:
                    window.location.href = this.component.customData.action.values ? this.component.customData.action.values[0] : '';
                    break;
                case ButtonActionType.Modal:
                    this.renderStore.showModal = true;
                    break;
                case ButtonActionType.Custom:
                    //if the action is a function, call it
                    if (typeof this.component.customData.action.values === 'function') {
                        this.component.customData.action.values();
                    }
                    break;
            }
        }
    }
});
</script>
